import { backendGet, backendPost, backendPatch } from "./dataService";
import { api } from "./dataService";

/**
 * Accept the document consent for a specific version
 * @param {number} versionId - The version ID to accept
 * @returns {Promise<Object>} The acceptance response
 */
export const acceptConsent = async (versionId) => {
  return backendPost("data/consent-documents/accept", {
    toSend: { version_id: versionId },
  });
};

/**
 * Check if the user needs to provide consent for any document type
 * @param {Object} consentStatuses - All consent statuses from the user object
 * @returns {Array} Array of document types needing consent
 */
export const getPendingConsentTypes = (consentStatuses) => {
  if (!consentStatuses) return [];
  return Object.entries(consentStatuses)
    .filter(([, status]) => status.required && status.needs_consent)
    .map(([type]) => type);
};

/**
 * Get the current version information if consent is needed
 * @param {Object} consentStatuses - All consent statuses from the user object
 * @param {string} documentType - The document type
 * @returns {Object|null} The current version info or null
 */
export const getCurrentVersion = (
  consentStatuses,
  documentType = "privacy"
) => {
  const status = consentStatuses?.[documentType];
  if (!status || !status.current_version) return null;
  return status.current_version;
};

/**
 * Download the PDF for a specific consent document version.
 * Returns a Blob suitable for creating an object URL.
 * @param {number} versionId
 * @returns {Promise<Blob>}
 */
export const downloadConsentDocument = async (versionId) => {
  const response = await api.get(
    `data/consent-documents/download/${versionId}`,
    {
      responseType: "blob",
    }
  );
  return response.data;
};

/**
 * Fetch consent document links configured to appear in the footer.
 * Only returns types that have show_in_footer enabled and an active document version.
 * @returns {Promise<Array<{label: string, version_id: number}>>}
 */
export const fetchFooterLinks = async () => {
  const response = await backendGet("data/consent-documents/footer-links");
  return response?.data ?? [];
};

// Admin API functions for document version management

/**
 * Fetch all document versions (admin)
 * @param {string} documentType - Optional filter by document type
 * @returns {Promise<Object>} List of all versions
 */
export const fetchAllVersions = async (documentType = null) => {
  const url = documentType
    ? `data/consent-documents/versions?type=${documentType}`
    : "data/consent-documents/versions";
  return backendGet(url);
};

/**
 * Create a new document version (admin)
 * @param {Object} versionData - The version metadata fields
 * @param {File} file - The PDF file
 * @returns {Promise<Object>} The created version
 */
export const createVersion = async (versionData, file, setError = null) => {
  const formData = new FormData();
  Object.entries(versionData).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    formData.append(
      key,
      typeof value === "boolean" ? (value ? "1" : "0") : value
    );
  });
  formData.append("document_file", file);
  return backendPost("data/consent-documents/versions", {
    toSend: formData,
    setError,
    options: { headers: { "Content-Type": "multipart/form-data" } },
  });
};

/**
 * Update a document version (admin)
 * @param {number} id - The version ID
 * @param {Object} versionData - The updated version data
 * @returns {Promise<Object>} The updated version
 */
export const updateVersion = async (id, versionData, setError = null) => {
  return backendPatch(`data/consent-documents/versions/${id}`, {
    toSend: versionData,
    setError,
  });
};
